import { useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { prefersReducedMotion } from "../lib/prefersReducedMotion";

interface RelativeRect {
  left: number;
  top: number;
}

// Take a FLIP's inline styles off the elements it moved, and forget them.
function clearFlipStyles(els: Set<HTMLElement>) {
  for (const el of els) {
    el.style.transition = "";
    el.style.transform = "";
  }
  els.clear();
}

/**
 * FLIP animation hook — animates children smoothly to new positions
 * when their order changes in the DOM.
 *
 * Attach `data-flip-id="<unique>"` to each trackable child element.
 * The hook captures positions after each render and, when it detects
 * movement, applies an inverse transform then transitions to zero.
 * A render that leaves `orderKey` unchanged while a FLIP is running skips
 * the capture; the positions are captured again when that FLIP ends.
 *
 * Positions are measured relative to the container so that container-level
 * movement (e.g., a sibling group expanding) doesn't trigger false animations.
 *
 * Before a new FLIP is measured, any in-progress FLIP is cancelled so that
 * getBoundingClientRect() returns true layout positions (not mid-animation
 * offsets). This prevents cascading phantom animations when renders happen
 * while a previous FLIP is still in flight.
 *
 * Respects `prefers-reduced-motion: reduce`.
 *
 * @param selector — CSS selector to find trackable elements (default: `[data-flip-id]`).
 *   The selected elements must have a `data-flip-id` attribute used as the stable key.
 * @param orderKey — Optional string that changes when the element set or order changes.
 *   When provided, FLIP animations only run when this key changes. This prevents
 *   false animations from sibling height changes (e.g., subtask expansion) that shift
 *   positions without reordering.
 */
export function useFlipAnimation(
  containerRef: React.RefObject<HTMLElement | null>,
  selector = "[data-flip-id]",
  orderKey?: string,
) {
  const prevRectsRef = useRef<Map<string, RelativeRect>>(new Map());
  const prevOrderKeyRef = useRef<string | undefined>(undefined);
  const cleanupTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const animatedElsRef = useRef<Set<HTMLElement>>(new Set());

  // Re-capture current visual positions as the baseline for the next FLIP
  // comparison. Call right before an unmount that was preceded by a
  // CSS-animated layout change (e.g., task-completion exit): without this,
  // FLIP compares pre-animation positions to post-unmount positions and
  // plays a redundant second animation on top of the CSS transition.
  const syncCurrentPositions = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const elements = container.querySelectorAll<HTMLElement>(selector);
    const newRects = new Map<string, RelativeRect>();
    elements.forEach((el) => {
      const id = el.dataset.flipId;
      if (!id) return;
      const rect = el.getBoundingClientRect();
      newRects.set(id, {
        left: rect.left - containerRect.left,
        top: rect.top - containerRect.top,
      });
    });
    prevRectsRef.current = newRects;
  }, [containerRef, selector]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      // Nothing is on screen (a collapsed board list), so nothing tracks the
      // order while it's gone. Forget the old positions: when the container
      // comes back, its first layout is the new baseline, not a slide from
      // wherever the elements stood before it went away. A FLIP still running
      // on the elements that went away ends here too: left running, it would
      // make the first render after the container comes back skip measuring.
      prevRectsRef.current = new Map();
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = undefined;
      clearFlipStyles(animatedElsRef.current);
      return;
    }

    // Respect reduced motion
    if (prefersReducedMotion()) {
      return;
    }

    // When orderKey is provided, only animate when it changes.
    // This filters out position shifts caused by sibling height changes
    // (e.g., subtask expansion) that don't represent actual reordering.
    const orderChanged = orderKey === undefined || orderKey !== prevOrderKeyRef.current;
    prevOrderKeyRef.current = orderKey;

    // A render that reorders nothing leaves a running FLIP alone: cancelling
    // it would snap the elements to their end positions mid-slide. The
    // baseline is captured again when the FLIP ends (see the cleanup timer).
    if (!orderChanged && animatedElsRef.current.size > 0) return;

    // Cancel any in-progress FLIP so getBoundingClientRect() returns true
    // layout positions, not mid-animation offsets. This runs before paint,
    // so the user never sees a flash. Clearing `transition` is not enough:
    // the running transition already ends at no transform, so the browser
    // keeps it going and the measurement reads the old offset — which the
    // next render then "animates" back, and the elements bounce. Only
    // `transition: none` stops it.
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = undefined;
    }
    const cancelledEls = [...animatedElsRef.current];
    for (const el of cancelledEls) {
      el.style.transition = "none";
      el.style.transform = "";
    }
    animatedElsRef.current.clear();

    // Force reflow so cleared styles take effect before measuring
    void container.offsetHeight;
    for (const el of cancelledEls) {
      el.style.transition = "";
    }

    const containerRect = container.getBoundingClientRect();
    const elements =
      container.querySelectorAll<HTMLElement>(selector);
    const newRects = new Map<string, RelativeRect>();
    const animations: {
      el: HTMLElement;
      deltaX: number;
      deltaY: number;
    }[] = [];

    elements.forEach((el) => {
      const id = el.dataset.flipId;
      if (!id) return;

      const elRect = el.getBoundingClientRect();
      const relRect: RelativeRect = {
        left: elRect.left - containerRect.left,
        top: elRect.top - containerRect.top,
      };
      newRects.set(id, relRect);

      if (!orderChanged) return;

      const oldRect = prevRectsRef.current.get(id);
      if (!oldRect) return;

      const deltaX = oldRect.left - relRect.left;
      const deltaY = oldRect.top - relRect.top;

      // Skip sub-pixel movements
      if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) return;

      animations.push({ el, deltaX, deltaY });
    });

    prevRectsRef.current = newRects;

    if (animations.length === 0) return;

    // Invert: move elements back to their old positions (no transition)
    for (const { el, deltaX, deltaY } of animations) {
      el.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
      animatedElsRef.current.add(el);
    }

    // Force reflow so the browser registers the inverse transform
    void container.offsetHeight;

    // Play: animate elements from old position to new position
    for (const { el } of animations) {
      el.style.transition =
        "transform 300ms cubic-bezier(0.22, 1, 0.36, 1)";
      el.style.transform = "";
    }

    // Cleanup inline styles after animation, then capture the positions
    // again: renders during the FLIP skipped the capture, and the layout may
    // have moved since it started (a filter's hidden cards shrinking, say).
    cleanupTimeoutRef.current = setTimeout(() => {
      clearFlipStyles(animatedElsRef.current);
      cleanupTimeoutRef.current = undefined;
      syncCurrentPositions();
    }, 350);
  });

  // The cleanup timer outlives the render that started it: a later render
  // may leave the FLIP running, so only a new FLIP or unmounting cancels it.
  // The FLIP's styles go with it, so a re-run effect (Fast Refresh) does not
  // find a running FLIP that no timer will end.
  useEffect(() => {
    const animatedEls = animatedElsRef.current;
    return () => {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = undefined;
      clearFlipStyles(animatedEls);
    };
  }, []);

  return syncCurrentPositions;
}
